import React, { FC, useRef } from "react";
import CopyToClipboard from "components/CopyToClipboard/CopyToClipboard";
import { IconType } from "enums/Icon";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { ParcelCol } from "../../../../styles/parcelsStyles";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import ImageViewer2 from "../../../../components/ImageViewer2/ImageViewer2";
import { getAddonsIconsImage } from "../../../Delivery/deliveryHelper";
import { authSelectAllowExtendedTools } from "../../../../reduxFolder/selectors/globalSelectors";
import { servicesSelectAddonsAll } from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  RedirectLink,
  RightArrowIcon,
} from "../../../Storage/components/StorageList/StorageList.styles";
import useInventoryItem, { InventoryItemChange } from "../useInventoryItem";
import {
  StepperButton,
  ButtonsWrapper,
  CheckboxAreaButton,
  Comment,
  ConsolidationNumber,
  Description,
  DownArrowIcon,
  ImageSection,
  InventoryItemRowContainer,
  LocationWrapper,
  ParcelDate,
  QuantityEdit,
  QuantityWrapper,
  StyledInput,
  UpArrowIcon,
  WeightWrapper,
} from "../../styles/Inventory.style";

interface InventoryItemRowProps {
  units: string;
  isSelected: boolean;
  allowExtendedTools: boolean;
  addons: any;
  onChange: (change: InventoryItemChange) => void;
  selectedInventoryItem: any;
  inventoryItem: any;
  isItemInCart: any;
  disabledParent: boolean;
  onFocusInventoryItemInPanel: (item: any) => void;
}

const InventoryItemRow: FC<InventoryItemRowProps> = React.memo(
  ({
    units,
    allowExtendedTools,
    addons,
    disabledParent,
    inventoryItem,
    selectedInventoryItem,
    isItemInCart,
    isSelected,
    onChange,
    onFocusInventoryItemInPanel,
  }) => {
    const itemRef = useRef(null);
    const { t } = useTranslation("common");
    const {
      item,
      pictures,
      hasPictures,
      weight,
      comment,
      itemAddons,
      parcelDate,
      isDecantItem,
      isCartableItem,
      quantity,
      hasQuantity,
      location,
      consolidationLink,
      selectedQuantity,
      handleCheckBoxClick,
      handleCheckboxAreaClick,
      handleQuantityChange,
      handleIncrease,
      handleDecrease,
      preventInputEvents,
    } = useInventoryItem({ inventoryItem, isItemInCart, isSelected, onChange });

    return (
      <InventoryItemRowContainer
        data-item-id={item.id}
        onClick={() => onFocusInventoryItemInPanel(inventoryItem)}
        selected={selectedInventoryItem === inventoryItem.get("id")}
      >
        <ParcelCol xs={9} sm={consolidationLink ? 6 : 7} md={6} lg={5}>
          <CheckboxAreaButton onClick={handleCheckboxAreaClick}>
            <Checkbox
              checked={isSelected || isItemInCart}
              onChange={handleCheckBoxClick}
              disabled={isItemInCart || !isCartableItem}
            />
          </CheckboxAreaButton>
          {hasPictures && (
            <ImageSection>
              <ImageViewer2
                images={pictures}
                zoomLevel={1}
                hideSideBar
                preventImageZoom
              />
            </ImageSection>
          )}
          <Description>
            <CopyToClipboard text={item.description ?? ""}>
              {item.description}
            </CopyToClipboard>
            {!itemAddons.isEmpty() && getAddonsIconsImage(itemAddons, addons)}
            {comment && <Comment>{comment}</Comment>}
          </Description>
        </ParcelCol>
        {hasQuantity && (
          <ParcelCol sm={2}>
            <QuantityWrapper>
              {isDecantItem && (
                <div>
                  {`${quantity} ${t("parcels.quantity").toLowerCase()}`}
                </div>
              )}
              {!isDecantItem && (
                <QuantityEdit>
                  <ButtonsWrapper>
                    <StepperButton
                      aria-label={t("common.increase")}
                      onClick={handleIncrease}
                      disabled={selectedQuantity === quantity}
                    >
                      <UpArrowIcon type={IconType.Arrow} />
                    </StepperButton>
                    <StyledInput
                      name="decantQuantity"
                      value={selectedQuantity}
                      onChange={handleQuantityChange}
                      onClick={preventInputEvents}
                    />
                    <StepperButton
                      aria-label={t("common.decrease")}
                      onClick={handleDecrease}
                      disabled={selectedQuantity === 1}
                    >
                      <DownArrowIcon type={IconType.Arrow} />
                    </StepperButton>
                  </ButtonsWrapper>
                  <div>{` / ${quantity} in stock`}</div>
                </QuantityEdit>
              )}
            </QuantityWrapper>
          </ParcelCol>
        )}
        <ParcelCol xs={3} sm={2}>
          <WeightWrapper>
            {!!weight && `${weight} ${t(`units.${units}.primaryShort`)}`}
            {item.color && <div>{item.color}</div>}
            {item.size && <div>{item.size}</div>}
            {consolidationLink && (
              <Visible xs>
                <ConsolidationNumber>
                  {consolidationLink.sku}
                </ConsolidationNumber>
              </Visible>
            )}
          </WeightWrapper>
        </ParcelCol>
        {allowExtendedTools && (
          <ParcelCol sm={1}>
            <LocationWrapper>{location}</LocationWrapper>
          </ParcelCol>
        )}
        <Hidden xs>
          <ParcelCol sm={consolidationLink ? 2 : 3}>
            <ParcelDate>{parcelDate}</ParcelDate>
          </ParcelCol>
        </Hidden>
        {consolidationLink && (
          <Hidden xs>
            <ParcelCol sm={2}>
              <RedirectLink
                to={consolidationLink.to}
                state={consolidationLink.state}
                onClick={(e) => e.stopPropagation()}
              >
                <span>{t("common.sentIn")}</span>
                <span>{consolidationLink.sku}</span>{" "}
                <RightArrowIcon type={IconType.RightArrow} />
              </RedirectLink>
            </ParcelCol>
          </Hidden>
        )}
      </InventoryItemRowContainer>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    allowExtendedTools: authSelectAllowExtendedTools,
    addons: servicesSelectAddonsAll,
  }),
);

export default withConnect(InventoryItemRow);
